import { useState, useCallback } from 'react';
import {
  getBasicChatCompletion,
  getStreamingChatCompletion,
  analyzeProductMatching,
  analyzeSupplierData,
  createProductEmbedding,
  moderateContent,
  generateProductDescription,
  categorizeProduct
} from '../services/openaiServices';

/**
 * Custom hook for OpenAI operations with loading states and error handling
 */
export const useOpenAI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingResponse, setStreamingResponse] = useState('');

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetStreamingResponse = useCallback(() => {
    setStreamingResponse('');
  }, []);

  const chatCompletion = useCallback(async (message, systemPrompt) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getBasicChatCompletion(message, systemPrompt);
      return response;
    } catch (err) {
      setError(err?.message || 'Failed to get chat completion');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const streamingChat = useCallback(async (message, systemPrompt) => {
    setLoading(true);
    setError(null);
    resetStreamingResponse();
    
    try {
      await getStreamingChatCompletion(
        message,
        (chunk) => {
          setStreamingResponse(prev => prev + chunk);
        },
        systemPrompt
      );
    } catch (err) {
      setError(err?.message || 'Failed to get streaming response');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [resetStreamingResponse]);

  const productMatching = useCallback(async (productData) => {
    setLoading(true);
    setError(null);
    
    try {
      const analysis = await analyzeProductMatching(productData);
      return analysis;
    } catch (err) {
      setError(err?.message || 'Failed to analyze product matching');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const supplierAnalysis = useCallback(async (supplierData) => {
    setLoading(true);
    setError(null);
    
    try {
      const analysis = await analyzeSupplierData(supplierData);
      return analysis;
    } catch (err) {
      setError(err?.message || 'Failed to analyze supplier data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createEmbedding = useCallback(async (text) => {
    setLoading(true);
    setError(null);
    
    try {
      const embedding = await createProductEmbedding(text);
      return embedding;
    } catch (err) {
      setError(err?.message || 'Failed to create embedding');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const moderateText = useCallback(async (text) => {
    setLoading(true);
    setError(null);
    
    try {
      const moderation = await moderateContent(text);
      return moderation;
    } catch (err) {
      setError(err?.message || 'Failed to moderate content');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const generateDescription = useCallback(async (productAttributes) => {
    setLoading(true);
    setError(null);
    
    try {
      const description = await generateProductDescription(productAttributes);
      return description;
    } catch (err) {
      setError(err?.message || 'Failed to generate description');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const categorizeProductData = useCallback(async (productData) => {
    setLoading(true);
    setError(null);
    
    try {
      const categorization = await categorizeProduct(productData);
      return categorization;
    } catch (err) {
      setError(err?.message || 'Failed to categorize product');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    streamingResponse,
    
    // Actions
    clearError,
    resetStreamingResponse,
    
    // OpenAI Operations
    chatCompletion,
    streamingChat,
    productMatching,
    supplierAnalysis,
    createEmbedding,
    moderateText,
    generateDescription,
    categorizeProductData,
  };
};

export default useOpenAI;